import { formatDate } from '../../../utility/dateUtils';
import {
  ABSENCE_ICONS,
  ABSENCE_FIELD_NAMES,
  ABSENCE_REQUEST_FIELD_NAMES,
} from '../../../constants/absence';
import { ABSENCE_REQUEST_STATUS_TYPES } from '../../../constants/statuses';
import { isUserDeactivated, getItemById } from '../../../utility/helpers';
import {
  prepareRequestPeriodLabel,
  prepareTableRequestDescription,
} from '../../../utility/absence';

export const prepareAbsenceRequestsPageTableActionsHeader = labels => [
  {
    id: 'actions-column',
    title: labels.actions,
    rowKey: 'actions',
    align: 'right',
    rowCellClass: 'actions-cell',
    isHeaderAction: true,
    minWidth: 108,
    maxWidth: 116,
  },
];

export const prepareAbsenceRequestsPageTableHeaders = (labels, classes) => [
  {
    id: 1,
    title: labels.for,
    rowKey: 'user',
    sortAs: 'user__first_name,user__last_name',
    minWidth: 160,
    maxWidth: '1fr',
  },
  {
    id: 2,
    title: labels.createdAt,
    rowKey: 'createdAt',
    sortAs: 'created_at',
    minWidth: 130,
    maxWidth: '1fr',
  },
  {
    id: 3,
    title: labels.description,
    rowKey: 'description',
    hasConditionalTooltip: true,
    isSortingDisabled: true,
    maxWidth: '1.5fr',
  },
  {
    id: 4,
    title: labels.absence,
    rowKey: 'absenceComponent',
    isSortingDisabled: true,
    maxWidth: 77,
  },
  {
    id: 5,
    title: labels.period,
    rowKey: 'period',
    hasConditionalTooltip: true,
    sortAs: 'absence_from',
    minWidth: 240,
    maxWidth: '1fr',
  },
  {
    id: 6,
    title: labels.status,
    rowKey: 'statusComponent',
    sortAs: 'status',
    minWidth: 106,
    maxWidth: 126,
  },
  {
    id: 7,
    title: labels.approvedBy,
    rowCellClass: classes.userActionColumn,
    rowKey: 'approvedBy',
    isSortingDisabled: true,
    minWidth: 45,
    maxWidth: '0.5fr',
  },
];

export const prepareAbsenceRequestsTableData = (
  translations,
  auth,
  canManageRequestsCheck,
  isDoubleApprove,
  absenceRequests,
  statuses,
  renderUser,
  renderStatus,
  renderAbsence,
  renderActions,
  renderApprovedByUsers
) => {
  if (!absenceRequests) return [];

  return absenceRequests.map(absenceRequest => {
    const { user } = absenceRequest;
    const hasUsedDays = absenceRequest?.used_days !== 0;
    const isRequestedByCurrentUser = user.id === auth.id;
    const isDeactivatedUser = isUserDeactivated(user);
    const isRequestManageableByCurrentUser = canManageRequestsCheck(user);
    const currentStatus = statuses.find(
      status => status.id === absenceRequest.status
    );
    const absenceIcon = getItemById(
      ABSENCE_ICONS,
      absenceRequest[ABSENCE_REQUEST_FIELD_NAMES.ABSENCE][
        ABSENCE_FIELD_NAMES.ICON
      ]
    )?.icon;
    const isRequestRejected =
      currentStatus.id === ABSENCE_REQUEST_STATUS_TYPES.REJECTED;
    const isRequestApproved =
      currentStatus.id === ABSENCE_REQUEST_STATUS_TYPES.APPROVED;
    const isHalfApproved =
      currentStatus?.id === ABSENCE_REQUEST_STATUS_TYPES.HALF_APPROVED;
    const isRequested =
      currentStatus?.id === ABSENCE_REQUEST_STATUS_TYPES.REQUESTED ||
      isHalfApproved;
    const isHalfApprovedBySuperior =
      isHalfApproved && absenceRequest.report_to && !absenceRequest.admin;
    const hasEdit =
      hasUsedDays &&
      ((isRequestManageableByCurrentUser && !isRequestRejected) ||
        (isRequestedByCurrentUser && isRequested)) &&
      !isHalfApprovedBySuperior &&
      !isDeactivatedUser;
    const hasDelete =
      isRequestManageableByCurrentUser ||
      (isRequestedByCurrentUser && !isRequestApproved);
    const hasManageRequestButtons =
      hasUsedDays &&
      (isDoubleApprove
        ? isRequestManageableByCurrentUser ||
          absenceRequest?.user?.report_to === auth.id
        : isRequestManageableByCurrentUser) &&
      isRequested &&
      !isDeactivatedUser;

    return {
      ...absenceRequest,
      user: renderUser(user),
      createdAt: formatDate(absenceRequest.created_at),
      period: prepareRequestPeriodLabel(
        translations,
        absenceRequest.absence_from,
        absenceRequest.absence_to,
        Math.abs(absenceRequest.used_days)
      ),
      description: prepareTableRequestDescription(
        absenceRequest[ABSENCE_REQUEST_FIELD_NAMES.ABSENCE],
        absenceRequest[ABSENCE_REQUEST_FIELD_NAMES.DESCRIPTION],
        absenceRequest[ABSENCE_REQUEST_FIELD_NAMES.REASON]
      ),
      statusComponent: renderStatus(currentStatus),
      absenceComponent: renderAbsence({
        ...absenceRequest[ABSENCE_REQUEST_FIELD_NAMES.ABSENCE],
        icon: absenceIcon,
      }),
      actions: renderActions(
        absenceRequest,
        hasManageRequestButtons,
        hasEdit,
        hasDelete
      ),
      approvedBy: renderApprovedByUsers(absenceRequest),
    };
  });
};

export const prepareNoResultsLabels = (
  translations,
  isLoading,
  isSearchActive
) => {
  if (isLoading) {
    return {
      title: '',
      content: '',
    };
  }

  return {
    title: isSearchActive ? '' : translations.title,
    content: isSearchActive ? translations.search : translations.message,
  };
};
